import React, { useState, useEffect } from 'react';
import './Header.css';

// Funzione per formattare la data (ora riceve una data stringa o un oggetto Date)
const getFormattedDate = (dateInput?: string | Date) => {
  const date = dateInput ? new Date(dateInput) : new Date();
  const datePart = new Intl.DateTimeFormat('it-IT', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: '2-digit'
  }).format(date).replace('.', '');
  const timePart = new Intl.DateTimeFormat('it-IT', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
  return `${datePart} ${timePart}`;
};

interface HeaderProps {
  userName?: string;
  isLoggedIn?: boolean;
  onLoginClick?: () => void;
  onLogout?: () => void;
  isMenuOpen?: boolean;
  onToggleMenu?: () => void;
}

const Header: React.FC<HeaderProps> = ({ userName, isLoggedIn, onLoginClick, onLogout, isMenuOpen, onToggleMenu }) => {
  const [currentDate, setCurrentDate] = useState<string>('Caricamento data...');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchServerTime = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Assumendo che il backend sia sulla stessa origine o CORS sia configurato
        // Dovresti usare l'URL completo del backend se sono su domini diversi,
        // magari da una variabile d'ambiente come abbiamo fatto per il server.
        // Per ora, assumo un path relativo.
        const response = await fetch('/api/server-time');
        if (!response.ok) {
          throw new Error(`Errore nel recuperare l'ora del server: ${response.statusText}`);
        }
        const data = await response.json();
        if (data.isoDateTime) {
          setCurrentDate(getFormattedDate(data.isoDateTime));
        } else {
          throw new Error('Formato data non valido dal server');
        }
      } catch (err: any) {
        console.error("Errore durante il fetch dell'ora del server:", err);
        setError('Data non disponibile');
        // Fallback all'ora locale del client in caso di errore, con indicazione
        setCurrentDate(getFormattedDate() + ' (client)'); 
      } finally {
        setIsLoading(false);
      }
    };

    fetchServerTime();

    // Opzionale: aggiorna periodicamente l'ora del server
    // Questo è più complesso perché ogni aggiornamento è una chiamata API
    const timer = setInterval(fetchServerTime, 60 * 1000); // aggiorna ogni minuto per tenere l'ora corrente

    return () => clearInterval(timer);
  }, []);

  // Visualizzazione condizionale basata su errore o stato di caricamento
  let dateDisplay = currentDate;
  if (isLoading) {
    dateDisplay = 'Caricamento data...';
  }
  // L'errore è già gestito nel fallback di currentDate, ma potresti voler un messaggio più specifico
  // if (error) {
  //   dateDisplay = error; 
  // }

  return (
    <header className="topnavbar-wrapper">
      <nav role="navigation" className="navbar">
        <div className="navbar-header">
          {isLoggedIn && (
            <button
              className={`hamburger-in-header ${isMenuOpen ? 'open' : ''}`}
              onClick={onToggleMenu}
              aria-label={isMenuOpen ? 'Chiudi menu' : 'Apri menu'}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          )}
          <div className="brand-logo">
            <img 
              src="data:image/png;base64,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" 
              alt="Dealmap logo" 
              className="img-responsive"
            />
            <span>{userName || 'DealMap'}</span>
          </div>
        </div>
        <div className="navbar-right-content">
          <span className="header-date">{dateDisplay}</span>
          {isLoggedIn ? (
            <button className="header-auth-btn header-auth-btn--logout" onClick={onLogout}>
              Esci
            </button>
          ) : (
            <button className="header-auth-btn header-auth-btn--login" onClick={onLoginClick}>
              Accedi
            </button>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header; 